import React, { useEffect } from 'react';
import { useMachine } from '@xstate/react';
import { object } from 'prop-types';
import { connect } from 'react-redux';
import querystring from 'querystring';
import {
  fetchMapProvidersV9,
  fetchMapLocationsV9,
  fetchMapProvidersByLocationV9,
  fetchMapSearchProvidersV9
} from '../behaviors/search-map-v9';
import {
  fetchSlots,
  fetchPurposesByProviderId,
  fetchProviderAvailability
} from '../behaviors/availability/actions';
import { getCustomerCode } from '../behaviors/customer-code';

import { createMapSearchMachine } from '../machines/map-search-v9/map-search-machine';
import { createMapSearchProviderListMachine } from '../machines/map-search-v9/map-search-machine-provider-list';

import { decorateMapSearchTracking } from './logging-decorators';

import MapSearch from '../map-search-v9';
import { pageViewEvent } from '../tracking/tracking-utils';
import { pages } from '../tracking/constants';
import { getSearchSummary } from '../utils/search-common';
import { productNameSelector } from '../behaviors/product-name';
import { settingsSelector } from 'Src/behaviors/settings';
import { isProviderListMapsEnabled } from 'Common/config';
import useMediaQuery from '@mui/material/useMediaQuery';
import Theme from '@kyruus/ui-theme';

function mapStateToProps(state, ownProps) {
  const appSettings = settingsSelector(state);
  const log = (message, event_data = {}) => {
    const additionalTrackingData = decorateMapSearchTracking({
      state,
      routeMatch: ownProps.match
    });

    ownProps.log(message, { ...event_data, ...additionalTrackingData });
  };

  return {
    locationsData: state.searchMapV9.locations,
    providersData: state.searchMapV9.providers,
    config: state.config,
    providersByLocation: state.searchMapV9.providersByLocation,
    log,
    customerCode: getCustomerCode(state),
    productName: productNameSelector(state),
    appSettings,
    slotsByProviderId: state.availability.slotsByProviderId,
    slotsStatusByProviderId: state.availability.slotsStatusByProviderId,
    purposeOptionsByProviderId: state.availability.purposeOptionsByProviderId,
    availabilityError: state.availability.error,
    purposesLoading: state.availability.purposesLoading
  };
}

function mapDispatchToProps(dispatch) {
  return {
    fetchProviders: (params) => {
      return dispatch(fetchMapProvidersV9(params));
    },
    fetchLocationsWithIds: (ids = []) => {
      return dispatch(
        fetchMapLocationsV9({
          // _filter is an OR field
          _filter: ids.map((id) => `id:${id}`)
        })
      );
    },
    fetchProvidersByLocation: (locationId, params) => {
      return dispatch(fetchMapProvidersByLocationV9(locationId, params));
    },
    fetchMapSearchProvidersV9: (params) => {
      return dispatch(fetchMapSearchProvidersV9(params));
    },
    fetchSlots: (apptInfo, providerIds) => {
      return dispatch(fetchSlots(apptInfo, providerIds));
    },
    fetchPurposesByProviderId: (providerId, log) => {
      return dispatch(fetchPurposesByProviderId(providerId, log));
    },
    fetchProviderAvailability: (providerId, apptInfo) => {
      return dispatch(fetchProviderAvailability(providerId, apptInfo));
    }
  };
}

function ProviderMapSearchContent(props) {
  const { location, fetchMapSearchProvidersV9 } = props;

  const queryParams = querystring.parse((location.search || '').substring(1));

  const [state, send] = useMachine(
    createMapSearchProviderListMachine({
      searchParams: getSearchSummary(queryParams),
      providers: undefined,
      fetchProviders: fetchMapSearchProvidersV9,
      config: props.config
    })
  );

  return <MapSearch state={state} send={send} {...props} />;
}

function MapSearchContent(props) {
  const {
    location,
    providersData,
    locationsData,
    fetchProviders,
    fetchLocationsWithIds,
    fetchProvidersByLocation,
    fetchSlots,
    config
  } = props;

  const queryParams = querystring.parse((location.search || '').substring(1));

  let deepLinkingParams;
  if (queryParams.location_id) {
    deepLinkingParams = {
      locationId: queryParams.location_id
    };
  }

  const [state, send] = useMachine(
    createMapSearchMachine({
      deepLinkingParams: deepLinkingParams,
      searchParams: getSearchSummary(queryParams),
      providers: providersData,
      locations: locationsData,
      fetchProviders: fetchProviders,
      fetchLocationsWithIds: fetchLocationsWithIds,
      fetchProvidersByLocation: fetchProvidersByLocation,
      fetchSlots: fetchSlots,
      config: config
    })
  );
  return <MapSearch state={state} send={send} {...props} />;
}

function MapSearchContainer(props) {
  const { log } = props;
  const isScreenMediumOrBelow = useMediaQuery(
    `(max-width:${Theme.screen_medium})`
  );

  useEffect(() => {
    // tracking on load of map-search page
    log(pageViewEvent(pages.MAP_SEARCH));
  }, []);

  const useProviderListMachine = isProviderListMapsEnabled(props.config);

  if (isScreenMediumOrBelow && useProviderListMachine) {
    return <ProviderMapSearchContent {...props} />;
  }
  return <MapSearchContent {...props} />;
}

MapSearchContainer.propTypes = {
  // react-router location object
  location: object
};

export default connect(mapStateToProps, mapDispatchToProps)(MapSearchContainer);
